import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnersApi, usersApi } from '@/lib/api';
import { Briefcase, Check, CircleAlert, Plus, Search } from 'lucide-react';
import {
  Button,
  Combobox,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  Pagination,
  Status,
  statusInfo,
  type Column,
  type DataTableSort,
} from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import { usePersistedFilters } from '@/hooks/usePersistedFilters';
import type { Partner, PartnerType, PartnerStatus } from '@/types';

const PER_PAGE = 50;

const typeLabels: Record<PartnerType, string> = {
  cleaning_company: 'Reinigungsfirma',
  netzwerkpartner: 'Netzwerkpartner',
  subcontractor: 'Subunternehmer',
  real_estate_agent: 'Immobilienmakler',
  architect: 'Architekt',
  other: 'Sonstige',
};

// Text und Ton aus STATUS des Design Systems (`partner_<status>`).
const PARTNER_STATUSES: PartnerStatus[] = ['active', 'inactive', 'prospect'];
const statusLabels = Object.fromEntries(
  PARTNER_STATUSES.map((s) => [s, statusInfo(`partner_${s}`).label]),
) as Record<PartnerStatus, string>;

function StatusDropdown({ partner }: { partner: Partner }) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (newStatus: PartnerStatus) =>
      partnersApi.update(partner.id, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} aria-label="Status ändern" className="cursor-pointer rounded-full">
          <Status status={`partner_${partner.status}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
        {PARTNER_STATUSES.map((value) => (
          <DropdownMenuItem
            key={value}
            onSelect={() => {
              if (value !== partner.status) mutation.mutate(value);
            }}
          >
            <span className="flex items-center justify-between gap-3">
              <Status status={`partner_${value}`} variant="plain" />
              {value === partner.status && <Check size={16} strokeWidth={1.75} className="text-highlight" aria-hidden />}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const defaultFilters = {
  search: '',
  partner_type: '' as PartnerType | '',
  status: '' as PartnerStatus | '',
  assigned_to: '',
  city: '',
  sort_by: 'name',
  sort_order: 'asc' as 'asc' | 'desc',
};

export function PartnersPage() {
  const navigate = useNavigate();
  const [filters, updateFilters, resetFilters] = usePersistedFilters('partners', defaultFilters);
  const [page, setPage] = useState(1);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['partners', { page, per_page: PER_PAGE, ...filters }],
    queryFn: () => partnersApi.list({
      page,
      per_page: PER_PAGE,
      search: filters.search || undefined,
      partner_type: filters.partner_type || undefined,
      status: filters.status || undefined,
      assigned_to: filters.assigned_to || undefined,
      city: filters.city || undefined,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
    }),
  });

  const sortieren = ({ key, direction }: DataTableSort) => {
    updateFilters({ sort_by: key, sort_order: direction });
    setPage(1);
  };

  const hasActiveFilters = filters.search || filters.partner_type || filters.status || filters.assigned_to || filters.city;

  // Am Handy eine Zeile je Partner: Name, darunter Status, Typ und Ort.
  const columns: Column<Partner>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      mobil: 'titel',
      renderMobil: (partner) => partner.name,
      render: (partner) => (
        <div>
          <div className="font-medium">{partner.name}</div>
          {partner.partner_number && <div className="t-mono text-muted-foreground">#{partner.partner_number}</div>}
        </div>
      ),
    },
    {
      key: 'partner_type',
      header: 'Typ',
      sortable: true,
      mobil: 'unter',
      render: (partner) => typeLabels[partner.partner_type],
    },
    {
      key: 'city',
      header: 'Ort',
      sortable: true,
      mobil: 'unter',
      render: (partner) => partner.city || '–',
    },
    {
      key: 'primary_contact',
      header: 'Ansprechpartner',
      render: (partner) =>
        partner.primary_contact_id ? (
          <Link to={`/contacts/${partner.primary_contact_id}`} className="text-link hover:underline" onClick={(e) => e.stopPropagation()}>
            {partner.primary_contact_name}
          </Link>
        ) : (
          <span className="text-muted-foreground">–</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobil: 'status',
      renderMobil: (partner) => <Status status={`partner_${partner.status}`} variant="plain" />,
      render: (partner) => <StatusDropdown partner={partner} />,
    },
  ];

  const totalPages = data?.pages ?? 0;

  if (error) {
    return (
      <>
        <CrmPageHeader title="Partner" />
        <EmptyState
          icon={CircleAlert}
          title="Fehler beim Laden"
          description="Die Partner ließen sich nicht laden. Seite neu laden oder später erneut versuchen."
        />
      </>
    );
  }

  return (
    <div>
      <CrmPageHeader
        title="Partner"
        subtitle={`${data?.total ?? 0} Partner insgesamt`}
        actions={
          <Button variant="primary" icon={Plus} onClick={() => navigate('/partners/new')}>
            Partner anlegen
          </Button>
        }
      >
        <div className="flex w-full flex-wrap items-center gap-3 pb-4">
          <Input
            icon={Search}
            placeholder="Suchen"
            aria-label="Partner suchen"
            value={filters.search}
            onChange={(e) => { updateFilters({ search: e.target.value }); setPage(1); }}
            className="min-w-[200px] flex-1"
          />
          <Combobox
            className="w-48"
            aria-label="Alle Typen"
            value={filters.partner_type}
            onChange={(v) => { updateFilters({ partner_type: v as PartnerType | '' }); setPage(1); }}
            options={Object.entries(typeLabels).map(([value, label]) => ({ value, label }))}
            placeholder="Alle Typen"
          />
          <Combobox
            className="w-44"
            aria-label="Alle Status"
            value={filters.status}
            onChange={(v) => { updateFilters({ status: v as PartnerStatus | '' }); setPage(1); }}
            options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
            placeholder="Alle Status"
          />
          <Input
            placeholder="Ort"
            aria-label="Nach Ort filtern"
            value={filters.city}
            onChange={(e) => { updateFilters({ city: e.target.value }); setPage(1); }}
            className="w-48"
          />
          <Combobox
            className="w-56"
            aria-label="Alle Mitarbeiter"
            value={filters.assigned_to}
            onChange={(v) => { updateFilters({ assigned_to: v }); setPage(1); }}
            options={users.map((u) => ({ value: u.id, label: u.name }))}
            placeholder="Alle Mitarbeiter"
          />
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={() => { resetFilters(); setPage(1); }}>
              Filter zurücksetzen
            </Button>
          )}
        </div>
      </CrmPageHeader>

      <DataTable<Partner>
        label="Partner"
        rows={data?.items ?? []}
        columns={columns}
        loading={isLoading}
        onRowClick={(partner) => navigate(`/partners/${partner.id}`)}
        empty={<EmptyState icon={Briefcase} title="Keine Partner gefunden" className="py-8" />}
        sort={{ key: filters.sort_by, direction: filters.sort_order }}
        onSortChange={sortieren}
      />

      {data && data.total > 0 && <Pagination page={page} pageCount={totalPages} onPageChange={setPage} />}
    </div>
  );
}
